import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import ApiService from '../../services/api';

interface WorkOrder {
  id: string;
  orderNumber: string;
  productCode: string;
  productName: string;
  quantity: number;
  completedQuantity: number;
  status: 'draft' | 'planned' | 'released' | 'started' | 'in_progress' | 'completed' | 'cancelled' | 'on_hold';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  startDate: string;
  dueDate: string;
  workCenter: string;
  assignedOperator?: string;
}

interface ProductionEvent {
  id: string;
  workOrderId: string;
  eventType: 'start' | 'pause' | 'stop' | 'complete' | 'quality_check';
  timestamp: string;
  operator: string;
  quantity?: number;
  notes?: string;
}

interface ProductionState {
  workOrders: WorkOrder[];
  productionEvents: ProductionEvent[];
  selectedWorkOrder: WorkOrder | null;
  isLoading: boolean;
  error: string | null;
}


// Normalise a raw API work order into our WorkOrder shape
function normaliseWorkOrder(raw: any): WorkOrder {
  return {
    id: String(raw.id ?? raw._id ?? ''),
    orderNumber: raw.orderNumber ?? raw.workOrderNumber ?? raw.order_number ?? `WO-${raw.id}`,
    productCode: raw.productCode ?? raw.itemCode ?? raw.product_code ?? '',
    productName: raw.productName ?? raw.itemDescription ?? raw.product_name ?? '',
    quantity: Number(raw.quantityPlanned ?? raw.quantity ?? raw.orderQuantity ?? raw.order_quantity ?? 0),
    completedQuantity: Number(raw.quantityCompleted ?? raw.completedQuantity ?? raw.completed_quantity ?? 0),
    status: raw.status ?? 'planned',
    priority: raw.priority ?? 'medium',
    startDate: raw.startDate ?? raw.plannedStartDate ?? raw.start_date ?? '',
    dueDate: raw.dueDate ?? raw.plannedEndDate ?? raw.due_date ?? '',
    workCenter: raw.workCenter ?? raw.workCenterCode ?? raw.work_center ?? '',
    assignedOperator: raw.assignedOperator ?? raw.assignedTo ?? raw.assigned_operator,
  };
}

const initialState: ProductionState = {
  workOrders: [],
  productionEvents: [],
  selectedWorkOrder: null,
  isLoading: false,
  error: null,
};

export const fetchWorkOrders = createAsyncThunk(
  'production/fetchWorkOrders',
  async (_, { rejectWithValue }) => {
    try {
      const data = await ApiService.getWorkOrders();
      // API may return { workOrders: [...] }, { data: [...] }, or a plain array
      const raw: any[] = Array.isArray(data)
        ? data
        : Array.isArray(data?.workOrders)
        ? data.workOrders
        : Array.isArray(data?.data)
        ? data.data
        : [];
      return raw.map(normaliseWorkOrder);
    } catch (error: any) {
      return rejectWithValue(error?.message ?? 'Failed to fetch work orders');
    }
  }
);

export const createWorkOrder = createAsyncThunk(
  'production/createWorkOrder',
  async (workOrderData: Partial<WorkOrder>) => {
    const data = await ApiService.createWorkOrder(workOrderData);
    return normaliseWorkOrder(data ?? workOrderData);
  }
);

export const updateWorkOrderStatus = createAsyncThunk(
  'production/updateStatus',
  async ({ id, status }: { id: string; status: WorkOrder['status'] }) => {
    try {
      // updateWorkOrder expects (id, body) — send partial update
      await ApiService.updateWorkOrder(id, { status } as any);
      return { id, status };
    } catch {
      // Optimistic update — still return to update local state
      return { id, status };
    }
  }
);

export const updateProductionQuantity = createAsyncThunk(
  'production/updateQuantity',
  async ({ id, quantity }: { id: string; quantity: number }) => {
    try {
      await ApiService.updateWorkOrder(id, { completedQuantity: quantity } as any);
    } catch {
      // Optimistic — continue
    }
    return { id, quantity };
  }
);

const productionSlice = createSlice({
  name: 'production',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setSelectedWorkOrder: (state, action: PayloadAction<WorkOrder | null>) => {
      state.selectedWorkOrder = action.payload;
    },
    addProductionEvent: (state, action: PayloadAction<ProductionEvent>) => {
      state.productionEvents.unshift(action.payload);
    },
    // Optimistic local delete before API confirmation
    removeWorkOrder: (state, action: PayloadAction<string>) => {
      state.workOrders = state.workOrders.filter(wo => wo.id !== action.payload);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchWorkOrders.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchWorkOrders.fulfilled, (state, action) => {
        state.isLoading = false;
        state.workOrders = action.payload;
      })
      .addCase(fetchWorkOrders.rejected, (state, action) => {
        state.isLoading = false;
        const message = typeof action.payload === 'string' ? action.payload : action.error.message;
        state.error = message ?? 'Failed to fetch work orders';
      })
      .addCase(createWorkOrder.fulfilled, (state, action) => {
        state.workOrders.unshift(action.payload);
      })
      .addCase(updateWorkOrderStatus.fulfilled, (state, action) => {
        const { id, status } = action.payload;
        const workOrder = state.workOrders.find(wo => wo.id === id);
        if (workOrder) {
          workOrder.status = status;
        }
      })
      .addCase(updateProductionQuantity.fulfilled, (state, action) => {
        const { id, quantity } = action.payload;
        const workOrder = state.workOrders.find(wo => wo.id === id);
        if (workOrder) {
          workOrder.completedQuantity = quantity;
        }
      });
  },
});

export const { clearError, setSelectedWorkOrder, addProductionEvent, removeWorkOrder } = productionSlice.actions;
export default productionSlice.reducer;
